import React, { Suspense, lazy } from 'react'
import { HashRouter, Navigate, Route, Routes } from 'react-router-dom'

import { useLocale } from '@/features/locale/LocaleProvider'

import AllowlistRouteGate from '../components/AllowlistRouteGate'
import MobileSwapRouteBootstrap from '../components/MobileSwapRouteBootstrap'
import Popups from '../components/Popups'
import { PlatformShell } from '../layout/PlatformShell'
import DarkModeQueryParamReader from '../theme/DarkModeQueryParamReader'
import { DeepSpaceViewportBackdrop } from '../theme'
import Home from './Home'
import { RedirectToHome } from './globalRedirects'
import {
  RedirectDuplicateTokenIds,
  RedirectOldAddLiquidityPathStructure,
  RedirectToAddLiquidity,
} from './AddLiquidity/redirects'
import { RedirectOldRemoveLiquidityPathStructure } from './RemoveLiquidity/redirects'
import { RedirectPathToSwapOnly } from './Swap/redirects'

const Explore = lazy(() => import('./Explore'))
const AddLiquidity = lazy(() => import('./AddLiquidity'))
const Bridge = lazy(() => import('./Bridge'))
const Dashboard = lazy(() => import('./Dashboard'))
const Portfolio = lazy(() => import('./Portfolio'))
const Pool = lazy(() => import('./Pool'))
const ImportPoolPage = lazy(() => import('./ImportPool'))
const RemoveLiquidity = lazy(() => import('./RemoveLiquidity'))
const History = lazy(() => import('./History'))
const ExplorePools = lazy(() => import('./ExplorePools'))
const PoolDetail = lazy(() => import('./PoolDetail'))
const TokenDetail = lazy(() => import('./TokenDetail'))
const TokenLiquidity = lazy(() => import('./TokenLiquidity'))
const EmbedAddLiquidityPage = lazy(() => import('./embed/EmbedAddLiquidityPage'))
const EmbedImportPoolPage = lazy(() => import('./embed/EmbedImportPoolPage'))
const EmbedPoolPage = lazy(() => import('./embed/EmbedPoolPage'))
const EmbedRemoveLiquidityPage = lazy(() => import('./embed/EmbedRemoveLiquidityPage'))
const EmbedSwapPage = lazy(() => import('./embed/EmbedSwapPage'))
const DiagnosticsRpcPage = lazy(() => import('./diagnostics/DiagnosticsRpcPage'))
const DesignSystemPage = lazy(() => import('./DesignSystem'))
const AboutPage = lazy(() => import('./About'))
const LegalPage = lazy(() => import('./Legal'))
const ContactPage = lazy(() => import('./Contact'))
const SettingsPage = lazy(() => import('./Settings'))
const Swap = lazy(() => import('./Swap'))

const suspenseFallback = null

export default function App() {
  useLocale()

  return (
    <Suspense fallback={suspenseFallback}>
      <HashRouter>
        <DarkModeQueryParamReader />
        <DeepSpaceViewportBackdrop />
        <MobileSwapRouteBootstrap />
        <PlatformShell>
          <Popups />
          <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/explore" element={<Explore />} />
              <Route path="/markets" element={<Navigate to="/explore?tab=markets" replace />} />
              <Route path="/token/:address" element={<TokenDetail />} />
              <Route path="/token/:address/liquidity" element={<TokenLiquidity />} />
              <Route path="/liquidity" element={<PoolDetail />} />
              <Route path="/liquidity/:poolId" element={<PoolDetail />} />
              <Route path="/explore/pools" element={<ExplorePools />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/legal" element={<LegalPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/settings" element={<Navigate to="/settings/account" replace />} />
              <Route path="/settings/:sectionId" element={<SettingsPage />} />
              <Route path="/portfolio" element={<Portfolio />} />
              <Route path="/portfolio/movements" element={<History />} />
              <Route path="/history" element={<Navigate to="/portfolio/movements" replace />} />
              <Route
                path="/bridge"
                element={
                  <AllowlistRouteGate>
                    <Bridge />
                  </AllowlistRouteGate>
                }
              />
              <Route
                path="/diagnostics/rpc"
                element={
                  <AllowlistRouteGate>
                    <DiagnosticsRpcPage />
                  </AllowlistRouteGate>
                }
              />
              <Route path="/profile" element={<Navigate to="/portfolio" replace />} />
              <Route
                path="/embed/liquidity/remove/:token0/:token1"
                element={
                  <AllowlistRouteGate>
                    <EmbedRemoveLiquidityPage />
                  </AllowlistRouteGate>
                }
              />
              <Route
                path="/embed/liquidity/add/:token0/:token1"
                element={
                  <AllowlistRouteGate>
                    <EmbedAddLiquidityPage />
                  </AllowlistRouteGate>
                }
              />
              <Route
                path="/embed/liquidity/add"
                element={
                  <AllowlistRouteGate>
                    <EmbedAddLiquidityPage />
                  </AllowlistRouteGate>
                }
              />
              <Route
                path="/embed/liquidity/positions"
                element={
                  <AllowlistRouteGate>
                    <EmbedPoolPage />
                  </AllowlistRouteGate>
                }
              />
              <Route
                path="/embed/liquidity/import"
                element={
                  <AllowlistRouteGate>
                    <EmbedImportPoolPage />
                  </AllowlistRouteGate>
                }
              />
              <Route
                path="/embed/swap"
                element={
                  <AllowlistRouteGate>
                    <EmbedSwapPage />
                  </AllowlistRouteGate>
                }
              />
              <Route
                path="/design-system"
                element={
                  <AllowlistRouteGate>
                    <DesignSystemPage />
                  </AllowlistRouteGate>
                }
              />
              <Route path="/swap" element={<Swap />} />
              <Route path="/send" element={<RedirectPathToSwapOnly />} />
              <Route path="/import" element={<ImportPoolPage />} />
              <Route path="/pool" element={<Pool />} />
              <Route path="/create" element={<RedirectToAddLiquidity />} />
              <Route path="/add" element={<AddLiquidity />} />
              <Route path="/add/:token0" element={<RedirectOldAddLiquidityPathStructure />} />
              <Route path="/add/:token0/:token1" element={<RedirectDuplicateTokenIds />} />
              <Route path="/remove/:tokens" element={<RedirectOldRemoveLiquidityPathStructure />} />
              <Route path="/remove/:token0/:token1" element={<RemoveLiquidity />} />
              <Route path="*" element={<RedirectToHome />} />
            </Routes>
        </PlatformShell>
      </HashRouter>
    </Suspense>
  )
}
